import React, { useState, useEffect, useCallback } from 'react';
import styles from './ImageViewer.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';

interface ImageOverlayProps {
    images: (File | string)[];
    initialIndex: number;
    onClose: () => void;
}

const ImageOverlay: React.FC<ImageOverlayProps> = ({ images, initialIndex, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoomLevel, setZoomLevel] = useState(1);

    // Support both File objects and string URLs
    const currentImageUrl = React.useMemo(() => {
        const item = images[currentIndex];
        if (typeof item === 'string') return item;
        return URL.createObjectURL(item);
    }, [images, currentIndex]);

    // Handle Keyboard Navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') handlePrevImage();
            if (e.key === 'ArrowRight') handleNextImage();
            if (e.key === '+' || e.key === '=') handleZoomIn();
            if (e.key === '-') handleZoomOut();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, currentIndex, zoomLevel]);

    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.5, 4));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.5, 0.5));

    const handlePrevImage = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setZoomLevel(1);
        }
    };

    const handleNextImage = () => {
        if (currentIndex < images.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setZoomLevel(1);
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = currentImageUrl;
        const currentItem = images[currentIndex];
        const name = typeof currentItem === 'string'
            ? currentItem.split('/').pop() || `evidence-${currentIndex + 1}`
            : currentItem.name || `evidence-${currentIndex + 1}`;

        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={styles.imageOverlay} onClick={onClose}>
            {/* Top Control Bar */}
            <div className={styles.topControlBar} onClick={(e) => e.stopPropagation()}>
                <div className={styles.actionGroup}>
                    <button className={styles.iconButton} onClick={handleZoomOut} title="Diminuir Zoom (-)">
                        <ZoomOutIcon fontSize="small" />
                    </button>
                    <span className={styles.counter}>
                        {Math.round(zoomLevel * 100)}%
                    </span>
                    <button className={styles.iconButton} onClick={handleZoomIn} title="Aumentar Zoom (+)">
                        <ZoomInIcon fontSize="small" />
                    </button>

                    <div className={styles.separator} />

                    <button className={styles.iconButton} onClick={handleDownload} title="Baixar Imagem">
                        <DownloadIcon fontSize="small" />
                    </button>

                    <div className={styles.separator} />

                    <span className={styles.counter}>
                        {currentIndex + 1} / {images.length}
                    </span>
                </div>

                <button className={styles.closeButton} onClick={onClose} title="Fechar (Esc)">
                    <CloseIcon />
                </button>
            </div>

            <div className={styles.mainImageContainer}>
                {currentIndex > 0 && (
                    <button className={`${styles.navButton} ${styles.prevButton}`} onClick={(e) => { e.stopPropagation(); handlePrevImage(); }} title="Anterior (Seta Esq)">
                        <ChevronLeftIcon fontSize="large" />
                    </button>
                )}

                <img
                    src={currentImageUrl}
                    alt={`Evidence ${currentIndex + 1}`}
                    className={styles.largeImage}
                    style={{ transform: `scale(${zoomLevel})` }}
                    onClick={(e) => e.stopPropagation()}
                />

                {currentIndex < images.length - 1 && (
                    <button className={`${styles.navButton} ${styles.nextButton}`} onClick={(e) => { e.stopPropagation(); handleNextImage(); }} title="Próximo (Seta Dir)">
                        <ChevronRightIcon fontSize="large" />
                    </button>
                )}
            </div>

            {/* Carousel */}
            {images.length > 1 && (
                <div className={styles.carouselContainer} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.carousel}>
                        {images.map((file, idx) => (
                            <img
                                key={idx}
                                src={typeof file === 'string' ? file : URL.createObjectURL(file)}
                                className={`${styles.carouselThumb} ${idx === currentIndex ? styles.carouselThumbActive : ''}`}
                                onClick={() => {
                                    setCurrentIndex(idx);
                                    setZoomLevel(1);
                                }}
                                alt={`Thumb ${idx}`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageOverlay;
